
import React from 'react';

interface LogJsonViewerProps {
    data: any;
}

const LogJsonViewer: React.FC<LogJsonViewerProps> = React.memo(({ data }) => {
    // Special handling for single string fields (like evaluation prompts)
    const renderValue = (value: any, key?: string): React.ReactNode => {
        if (typeof value === 'string' && value.length > 200) {
            // Long strings - render with proper newlines
            return (
                <div key={key} className="mb-4">
                    {key && <div className="text-emerald-400 font-bold text-xs mb-2 uppercase">{key}</div>}
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                        <pre className="text-xs font-mono text-slate-300 whitespace-pre-wrap leading-relaxed">
                            {value}
                        </pre>
                    </div>
                </div>
            );
        }
        return null;
    };

    // Check if data is object with string values (like our prompts)
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        const entries = Object.entries(data);
        const hasLongStrings = entries.some(([_, v]) => typeof v === 'string' && (v as string).length > 200);

        if (hasLongStrings) {
            return (
                <div className="space-y-4">
                    {entries.map(([key, value]) => renderValue(value, key) || (
                        <div key={key} className="mb-2">
                            <span className="text-emerald-400 font-bold text-xs">{key}: </span>
                            <span className="text-indigo-300 text-xs font-mono">
                                {typeof value === 'string' ? value : JSON.stringify(value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
    }

    // Default: JSON format
    const formattedJson = React.useMemo(() => JSON.stringify(data, null, 2), [data]);
    return (
        <div className="bg-slate-900 rounded-3xl p-6 shadow-xl relative group">
            <pre className="text-xs font-mono text-indigo-300 overflow-x-auto custom-scrollbar-dark whitespace-pre-wrap leading-relaxed">
                {formattedJson}
            </pre>
        </div>
    );
});

export default LogJsonViewer;
